/** Configuration des modules : visibilité, ordre, modules utilisateur. */
import * as React from 'react';

const STORAGE_KEY = 'ligam-modules-config';
const STORAGE_VERSION = 1;

export interface UserModule {
  id: string; // ex. "u-1717590000000"
  label: string;
  createdAt: number;
}

export interface ModulesConfig {
  hiddenIds: string[];
  order: string[]; // ordered ids (fixed + user); missing ones are appended in default order
  userModules: UserModule[];
  /** Modules patient masqués (ids: clinical, civil, billing, history, media, exercises). */
  patientHiddenIds: string[];
  /** Ordre des modules patient. Ids manquants → ajoutés à la fin. */
  patientOrder: string[];
}

const DEFAULT_CONFIG: ModulesConfig = {
  hiddenIds: [],
  order: [],
  userModules: [],
  patientHiddenIds: [],
  patientOrder: [],
};

function load(): ModulesConfig {
  if (typeof localStorage === 'undefined') return { ...DEFAULT_CONFIG };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_CONFIG };
    const parsed = JSON.parse(raw) as { v?: number; config?: ModulesConfig };
    if (parsed?.v !== STORAGE_VERSION || !parsed.config)
      return { ...DEFAULT_CONFIG };
    return {
      hiddenIds: Array.isArray(parsed.config.hiddenIds)
        ? parsed.config.hiddenIds
        : [],
      order: Array.isArray(parsed.config.order) ? parsed.config.order : [],
      userModules: Array.isArray(parsed.config.userModules)
        ? parsed.config.userModules
        : [],
      patientHiddenIds: Array.isArray(parsed.config.patientHiddenIds)
        ? parsed.config.patientHiddenIds
        : [],
      patientOrder: Array.isArray(parsed.config.patientOrder)
        ? parsed.config.patientOrder
        : [],
    };
  } catch {
    return { ...DEFAULT_CONFIG };
  }
}

function save(config: ModulesConfig): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ v: STORAGE_VERSION, config }),
    );
  } catch {
    /* non-fatal */
  }
}

type Listener = (config: ModulesConfig) => void;
const listeners = new Set<Listener>();

function notify(): void {
  const c = load();
  listeners.forEach((l) => l(c));
}

export function subscribeModulesConfig(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function getModulesConfig(): ModulesConfig {
  return load();
}

export function toggleModuleVisibility(id: string): void {
  const c = load();
  const isHidden = c.hiddenIds.includes(id);
  const next: ModulesConfig = {
    ...c,
    hiddenIds: isHidden
      ? c.hiddenIds.filter((x) => x !== id)
      : [...c.hiddenIds, id],
  };
  save(next);
  notify();
}

export function setModulesOrder(orderedIds: string[]): void {
  const c = load();
  save({ ...c, order: orderedIds });
  notify();
}

export function togglePatientModuleVisibility(id: string): void {
  const c = load();
  const isHidden = c.patientHiddenIds.includes(id);
  const next: ModulesConfig = {
    ...c,
    patientHiddenIds: isHidden
      ? c.patientHiddenIds.filter((x) => x !== id)
      : [...c.patientHiddenIds, id],
  };
  save(next);
  notify();
}

export function setPatientModulesOrder(orderedIds: string[]): void {
  const c = load();
  save({ ...c, patientOrder: orderedIds });
  notify();
}

export function addUserModule(label: string): UserModule {
  const trimmed = label.trim() || 'Nouveau module';
  const c = load();
  const mod: UserModule = {
    id: `u-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    label: trimmed,
    createdAt: Date.now(),
  };
  save({
    ...c,
    userModules: [...c.userModules, mod],
    order: [...c.order, mod.id],
  });
  notify();
  return mod;
}

export function renameUserModule(id: string, label: string): void {
  const trimmed = label.trim();
  if (!trimmed) return;
  const c = load();
  save({
    ...c,
    userModules: c.userModules.map((m) =>
      m.id === id ? { ...m, label: trimmed } : m,
    ),
  });
  notify();
}

export function removeUserModule(id: string): void {
  const c = load();
  save({
    ...c,
    userModules: c.userModules.filter((m) => m.id !== id),
    order: c.order.filter((x) => x !== id),
    hiddenIds: c.hiddenIds.filter((x) => x !== id),
  });
  // Also remove its docking storage
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(`ligam-docking-${id}`);
    } catch {
      /* non-fatal */
    }
  }
  notify();
}

/** Hook React qui réagit aux changements de config. */
export function useModulesConfig(): ModulesConfig {
  const [config, setConfig] = React.useState<ModulesConfig>(() => load());
  React.useEffect(() => {
    return subscribeModulesConfig((c) => setConfig(c));
  }, []);
  return config;
}

/** Trie une liste d'ids selon `order`, en ajoutant les ids manquants à la fin. */
export function applyOrder<T extends { id: string }>(
  items: T[],
  order: string[],
): T[] {
  const indexOf = new Map<string, number>();
  order.forEach((id, i) => indexOf.set(id, i));
  return [...items].sort((a, b) => {
    const ai = indexOf.has(a.id) ? indexOf.get(a.id)! : Number.MAX_SAFE_INTEGER;
    const bi = indexOf.has(b.id) ? indexOf.get(b.id)! : Number.MAX_SAFE_INTEGER;
    return ai - bi;
  });
}
